/**
 * 分镜节点面板（刀A）：编辑单个分镜节点承载的那一镜——场景/出场角色/动作 +
 * 景别·机位·运镜·光影·色彩·情绪·转场·时长（一行三个的紧凑规格表）+ 台词。
 * 只负责渲染与产出 patch，写回 store 由编辑器负责。下游关键帧/视频将连本节点取具体某镜。
 */
import type { StoryboardShot } from '../../model/generation';
import type { ShotPayload } from '../../model/generation';

const field =
  'w-full rounded-control bg-surface-base px-2 py-1 text-uiSm text-ink-primary outline-none focus:bg-surface-raised';

/** 带“参数名：”前缀的小字段（紧凑规格表用） */
function ShotField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex min-w-0 items-baseline gap-1 text-uiSm">
      <span className="shrink-0 text-ink-faint">{label}：</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 rounded-control bg-surface-base px-1.5 py-0.5 text-ink-primary outline-none focus:bg-surface-raised"
      />
    </label>
  );
}

const splitList = (v: string): string[] =>
  v.split(/[、,，]/).map((s) => s.trim()).filter(Boolean);

interface ShotNodePanelProps {
  payload: ShotPayload;
  onPatch: (patch: Partial<Omit<StoryboardShot, 'id' | 'index'>>) => void;
  onKeyframe?: () => void;
  onAudio?: () => void;
  onVideo?: () => void;
}

const actionLink = 'text-ink-secondary transition-colors hover:text-ink-primary hover:underline disabled:opacity-40';

export function ShotNodePanel({ payload, onPatch, onKeyframe, onAudio, onVideo }: ShotNodePanelProps) {
  const sh = payload.shot;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-uiSm text-ink-faint">
        第 {payload.episodeIndex} 集 · 第 {sh.index} 镜
      </div>
      <ShotField label="场景" value={sh.scene} onChange={(v) => onPatch({ scene: v })} />
      <label className="flex flex-col gap-0.5 text-uiSm">
        <span className="text-ink-faint">画面动作：</span>
        <textarea className={`${field} min-h-12 resize-none`} value={sh.action} onChange={(e) => onPatch({ action: e.target.value })} />
      </label>
      <div className="grid grid-cols-3 gap-x-2 gap-y-1">
        <ShotField label="角色" value={sh.characters.join('、')} onChange={(v) => onPatch({ characters: splitList(v) })} />
        <ShotField label="道具" value={sh.props.join('、')} onChange={(v) => onPatch({ props: splitList(v) })} />
        <ShotField label="景别" value={sh.shotSize} onChange={(v) => onPatch({ shotSize: v })} />
        <ShotField label="机位" value={sh.cameraAngle} onChange={(v) => onPatch({ cameraAngle: v })} />
        <ShotField label="运镜" value={sh.cameraMove} onChange={(v) => onPatch({ cameraMove: v })} />
        <ShotField label="光影" value={sh.lighting} onChange={(v) => onPatch({ lighting: v })} />
        <ShotField label="色彩" value={sh.colorTone} onChange={(v) => onPatch({ colorTone: v })} />
        <ShotField label="情绪" value={sh.emotion} onChange={(v) => onPatch({ emotion: v })} />
        <ShotField label="转场" value={sh.transition} onChange={(v) => onPatch({ transition: v })} />
        <label className="flex items-baseline gap-1 text-uiSm">
          <span className="shrink-0 text-ink-faint">时长：</span>
          <input
            type="number"
            min={0}
            value={sh.durationSec}
            onChange={(e) => onPatch({ durationSec: Number(e.target.value) || 0 })}
            className="w-11 rounded-control bg-surface-base px-1.5 py-0.5 text-ink-primary outline-none focus:bg-surface-raised"
          />
          <span className="text-ink-faint">秒</span>
        </label>
      </div>
      <ShotField label="台词" value={sh.dialogue} onChange={(v) => onPatch({ dialogue: v })} />
      {/* 完备性补充（旧版 8 层）：场景描述/造型视觉/音效/台词音色 */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
        <label className="flex flex-col gap-0.5 text-uiSm">
          <span className="text-ink-faint">场景描述：</span>
          <textarea className={`${field} min-h-10 resize-none`} value={sh.scene_description} onChange={(e) => onPatch({ scene_description: e.target.value })} />
        </label>
        <label className="flex flex-col gap-0.5 text-uiSm">
          <span className="text-ink-faint">造型视觉：</span>
          <textarea className={`${field} min-h-10 resize-none`} value={sh.character_visual} onChange={(e) => onPatch({ character_visual: e.target.value })} />
        </label>
        <label className="flex flex-col gap-0.5 text-uiSm">
          <span className="text-ink-faint">音效/BGM：</span>
          <textarea className={`${field} min-h-10 resize-none`} value={sh.sound_design} onChange={(e) => onPatch({ sound_design: e.target.value })} />
        </label>
        <label className="flex flex-col gap-0.5 text-uiSm">
          <span className="text-ink-faint">台词音色：</span>
          <textarea className={`${field} min-h-10 resize-none`} value={sh.voice_direction} onChange={(e) => onPatch({ voice_direction: e.target.value })} />
        </label>
      </div>
      {/* 下游链：本镜→关键帧/配音/分镜视频（均连线到本分镜节点，取本镜内容） */}
      {(onKeyframe !== undefined || onAudio !== undefined || onVideo !== undefined) && (
        <div className="flex shrink-0 items-center gap-2 border-t border-surface-line pt-2 text-ui">
          {onKeyframe !== undefined && (
            <button type="button" className={actionLink} onClick={onKeyframe}>
              生成关键帧
            </button>
          )}
          {onKeyframe !== undefined && onAudio !== undefined && <span className="text-ink-faint">·</span>}
          {onAudio !== undefined && (
            <button type="button" className={actionLink} onClick={onAudio}>
              加配音
            </button>
          )}
          {(onAudio !== undefined || onKeyframe !== undefined) && onVideo !== undefined && <span className="text-ink-faint">·</span>}
          {onVideo !== undefined && (
            <button type="button" className={actionLink} onClick={onVideo}>
              分镜视频
            </button>
          )}
        </div>
      )}
      <p className="text-uiSm leading-snug text-ink-faint">
        本镜已独立成卡：后续把「分镜关键帧 / 配音 / 分镜视频」连到这张卡上，即可按本镜内容取料（不再连整张导演剧本）。
      </p>
    </div>
  );
}
